import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Helpers
import cloneDeep from "lodash.clonedeep";
import { ScrollDisable } from "../../../../helpers/ScrollLock";

// Custom components
import OptionsModal from "../../../../components/OptionsModal/OptionsModal";
import DeletionModal from "../../../../components/OptionsModal/OptionsModalMenus/DeletionModal";
import toastManager from "../../../../helpers/toastManager";
import PasswordRequirementsCard from "../../../../components/Authentication/PasswordRequirementsCard";
import DemoLimitationBanner from "../../../../components/DemoLimitationBanner/DemoLimitationBanner";

import { Heading1 } from "../../../../components/Headings/Headings";
import {
  SquareIconNavButton,
  TextActionButton,
} from "../../../../components/Buttons/Buttons";
import {
  DualRowItem,
  RegularField,
  PasswordField,
  SelectField,
} from "../../../../components/InputFields/InputFields";

// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  selectUserData,
  setUserData,
  logoutUser,
} from "../../../../redux/features/userState/userStateSlice";
import { resetPatient } from "../../../../redux/features/patientState/patientStateSlice";
import { resetApplication } from "../../../../redux/features/applicationState/applicationStateSlice";

// API imports
import useAxios from "../../../../hooks/useAxios";
import jsonFormatAuth from "../../../../apis/jsonFormatAuth";

// Icons
import { ArrowPathIcon, TrashIcon } from "@heroicons/react/24/outline";

const ProfileScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userData = useSelector(selectUserData);

  const [deleteOpen, setDeleteOpen] = useState(false);

  const DEPLOYMENT_TYPE = process.env.REACT_APP_DEPLOYMENT_TYPE;
  const disableFields = DEPLOYMENT_TYPE === "demo";
  const showLimitationBanner = DEPLOYMENT_TYPE === "demo";

  const handleDeleteClick = () => {
    if (username && password) {
      ScrollDisable();
      setDeleteOpen(true);
    } else {
      !password ? setPasswordWarning(true) : setPasswordWarning(false);
      toastManager(
        "warning",
        "Profile cannot be deleted.",
        "Enter password to open deletion options."
      );
    }
  };

  // API Start
  const { response, error, axiosFetch } = useAxios();

  const getData = () => {
    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "GET",
      url: "user/profile",
      sendSignal: false,
      requestConfig: {},
    });
  };

  useEffect(() => {
    // Obtain data from API on initial page load.
    getData();
  }, []);

  const handleSubmit = () => {
    if (password && username && email && firstName && lastName) {
      if (
        (newPassword.length === 0 && newPasswordConfirm.length === 0) ||
        ((newPassword.length > 0 || newPasswordConfirm.length > 0) &&
          lowercasePresent &&
          uppercasePresent &&
          numberPresent &&
          specialPresent &&
          lengthPresent &&
          matchingPresent)
      ) {
        axiosFetch({
          axiosInstance: jsonFormatAuth(),
          method: "POST",
          url: "user/profile",
          sendSignal: false,
          requestConfig: {
            username: username,
            email: email,
            password: password,
            new_password: newPassword,
            new_password_confirm: newPasswordConfirm,
            full_name: fullName ? fullName : `${firstName} ${lastName}`,
            first_name: firstName,
            last_name: lastName,
            position_title: positionTitle,
            physician_work_id: physicianWorkId,
            phone_number_one: phoneOne,
            phone_number_two: phoneTwo,
            address: address,
            birth_date: birthDate,
            sex: sex,
            description: description,
          },
        });
      } else {
        toastManager(
          "warning",
          "Profile cannot be updated.",
          "New password must meet security standards."
        );
      }
      newPassword !== newPasswordConfirm
        ? setNewPasswordWarning(true)
        : setNewPasswordWarning(false);
      newPassword !== newPasswordConfirm
        ? setNewPasswordConfirmWarning(true)
        : setNewPasswordConfirmWarning(false);
    } else {
      toastManager(
        "warning",
        "Profile cannot be updated.",
        "Please fill out all of the required fields."
      );
    }

    !password ? setPasswordWarning(true) : setPasswordWarning(false);
    !username ? setUsernameWarning(true) : setUsernameWarning(false);
    !email ? setEmailWarning(true) : setEmailWarning(false);
    !firstName ? setFirstNameWarning(true) : setFirstNameWarning(false);
    !lastName ? setLastNameWarning(true) : setLastNameWarning(false);
  };

  const handleDelete = () => {
    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "DELETE",
      url: "user/profile",
      sendSignal: false,
      requestConfig: {
        data: {
          delete_confirmation: true,
          username: username,
          email: email,
          password: password,
        },
      },
    });
  };

  // RESPONSE Detection
  useEffect(() => {
    // 1: Custom action code handling.
    if (response?.action_code === "profile_details_sent") {
      if (response?.payload) {
        const profileDataResponse = response.payload?.profile_data;

        if (profileDataResponse) {
          setUsername(profileDataResponse?.username || "");
          setEmail(profileDataResponse?.email || "");
          setPassword("");
          setNewPassword("");
          setNewPasswordConfirm("");
          setFullName(profileDataResponse?.full_name || "");
          setFirstName(profileDataResponse?.first_name || "");
          setLastName(profileDataResponse?.last_name || "");
          setPositionTitle(profileDataResponse?.position_title || "");
          setPhysicianWorkId(profileDataResponse?.physician_work_id || "");
          setPhoneOne(profileDataResponse?.phone_number_one || "");
          setPhoneTwo(profileDataResponse?.phone_number_two || "");
          setAddress(profileDataResponse?.address || "");
          setBirthDate(profileDataResponse?.birth_date || "");
          setSex(profileDataResponse?.sex || "");
          setDescription(profileDataResponse?.description || "");

          // Update username in header.
          if (profileDataResponse?.username) {
            const updatedUserData = cloneDeep(userData);
            updatedUserData.username = profileDataResponse?.username;
            dispatch(setUserData(updatedUserData));
          }
        }
      }
    }

    // Profile details have been updated.
    if (response?.action_code === "profile_updated") {
      toastManager(
        "success",
        "Profile Updated Successfully!",
        "Profile information has been updated successfully."
      );
      // Request data so the user can verify changes were saved or not.
      getData();
    }

    // Account has been successfully deleted.
    if (response?.action_code === "profile_deleted") {
      navigate("/login");

      dispatch(resetApplication());
      dispatch(logoutUser());
      dispatch(resetPatient());

      toastManager(
        "success",
        "Profile Deleted Successfully!",
        "All data associated with this profile has been removed from the server."
      );
    }
  }, [response]);

  // ERROR Detection
  useEffect(() => {
    if (error) {
      // 1: Custom action_code handling.
      // Username and password doesn't match to user.
      if (error?.authentication?.action_code === "authentication_failed") {
        toastManager("error", "Unauthorized!", "Invalid username or password.");
      }

      // Password errors.
      if (
        error?.new_password?.action_code === "password_issue" ||
        error?.new_password_confirm?.action_code === "password_issue"
      ) {
        toastManager(
          "warning",
          "New Password Issue",
          "Ensure new password and confirmation password match."
        );
      }

      // Username errors.
      // Username taken error.
      if (error?.username?.action_code === "username_taken") {
        toastManager(
          "warning",
          "Username Taken!",
          "This username is already taken. Use a different username."
        );
      }

      // Front-end username does not match during deletion.
      if (error?.username?.action_code === "authentication_failed") {
        toastManager(
          "warning",
          "Username does not match!",
          "This username does not to the one in the database. Enter the correct username."
        );
      }
    }
  }, [error]);

  // API End

  // User
  const [username, setUsername] = useState("");
  const [usernameWarning, setUsernameWarning] = useState(false);

  const [email, setEmail] = useState("");
  const [emailWarning, setEmailWarning] = useState(false);

  const [password, setPassword] = useState("");
  const [passwordWarning, setPasswordWarning] = useState(false);

  // Password change
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordWarning, setNewPasswordWarning] = useState(false);
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [newPasswordConfirmWarning, setNewPasswordConfirmWarning] =
    useState(false);

  // Profile
  const [fullName, setFullName] = useState("");

  const [firstName, setFirstName] = useState("");
  const [firstNameWarning, setFirstNameWarning] = useState(false);

  const [lastName, setLastName] = useState("");
  const [lastNameWarning, setLastNameWarning] = useState(false);

  const [positionTitle, setPositionTitle] = useState("");
  const [physicianWorkId, setPhysicianWorkId] = useState("");

  const [phoneOne, setPhoneOne] = useState("");
  const [phoneTwo, setPhoneTwo] = useState("");

  const [address, setAddress] = useState("");

  const [birthDate, setBirthDate] = useState("");
  const [sex, setSex] = useState("");

  const [description, setDescription] = useState("");

  // Password Verifier
  const [lowercasePresent, setLowercasePresent] = useState(false);
  const [uppercasePresent, setUppercasePresent] = useState(false);
  const [numberPresent, setNumberPresent] = useState(false);
  const [specialPresent, setSpecialPresent] = useState(false);
  const [lengthPresent, setLengthPresent] = useState(false);
  const [matchingPresent, setMatchingPresent] = useState(false);

  return (
    <div className="flex flex-col w-full pb-4 md:pb-0 Xbg-red-500">
      {deleteOpen && (
        <OptionsModal exitFunction={setDeleteOpen} setValue={false}>
          <DeletionModal
            warningTitle={"Delete Dermi.ai Account?"}
            warningParagraph={
              "You are about to delete your Dermi.ai account. This will result in the permanent deletion of all your data from all Dermi.ai systems. Please remember, once your account has been deleted, the action is irreversible. Please consider carefully before proceeding."
            }
            confirmationSentence={"PERMANENTLY DELETE MY DERMI.AI ACCOUNT"}
            cancelFunction={() => {
              setDeleteOpen(false);
            }}
            confirmFunction={handleDelete}
          />
        </OptionsModal>
      )}

      <div className="flex flex-col md:flex-row items-center justify-between w-full">
        <div className="flex flex-row items-center justify-start w-full Xbg-green-500">
          <Heading1 headingText={"Profile Details"} />
        </div>
        <div className="flex flex-row space-x-4 items-center justify-end w-full h-full pt-5 pb-2 Xbg-yellow-500">
          <SquareIconNavButton
            Icon={ArrowPathIcon}
            actionFunction={getData}
            activeColor={"active:bg-cyan-100"}
          />

          <SquareIconNavButton
            Icon={TrashIcon}
            actionFunction={handleDeleteClick}
            activeColor={"active:bg-red-100"}
          />
        </div>
      </div>

      <div className="py-2" />

      {showLimitationBanner && (
        <DemoLimitationBanner
          limitationTitle={"Demo Limitations"}
          limitationDescription={
            <>
              For privacy protection, input is restricted in the online{" "}
              <i>Clinical Dashboard Demo</i>. Personal detail saving is limited.
            </>
          }
        />
      )}

      {/* Username and email */}
      <DualRowItem
        leftChild={
          <RegularField
            fieldLabel="Username"
            placeholderText="e.g., doc_1 (required)"
            fieldType="text"
            value={username}
            fieldWarning={usernameWarning}
            takeFullWidth={true}
            customOnChange={(event) => {
              // Prevent spaces
              let newUsername = event.target.value.replace(/\s/g, "");
              setUsername(newUsername);
            }}
          />
        }
        rightChild={
          <RegularField
            fieldLabel={"Email"}
            placeholderText={"e.g., john-smith@mail.com"}
            fieldType="email"
            setFunction={setEmail}
            value={email}
            fieldWarning={emailWarning}
            takeFullWidth={true}
          />
        }
      />

      {/* Current password */}
      <PasswordField
        passwordLabel="Current Password"
        placeholderText="(required for any changes)"
        setFunction={setPassword}
        value={password}
        passwordWarning={passwordWarning}
        takeFullWidth={true}
      />

      {(newPassword || newPasswordConfirm) && (
        <PasswordRequirementsCard
          password={newPassword}
          setPasswordWarning={setNewPasswordWarning}
          passwordConfirm={newPasswordConfirm}
          setPasswordConfirmWarning={setNewPasswordConfirmWarning}
          lowercasePresent={lowercasePresent}
          setLowercasePresent={setLowercasePresent}
          uppercasePresent={uppercasePresent}
          setUppercasePresent={setUppercasePresent}
          numberPresent={numberPresent}
          setNumberPresent={setNumberPresent}
          specialPresent={specialPresent}
          setSpecialPresent={setSpecialPresent}
          lengthPresent={lengthPresent}
          setLengthPresent={setLengthPresent}
          matchingPresent={matchingPresent}
          setMatchingPresent={setMatchingPresent}
          takeFullWidth={true}
        />
      )}

      {/* New password and new password confirmation */}
      <DualRowItem
        leftChild={
          <PasswordField
            passwordLabel="New Password"
            placeholderText="(optional)"
            setFunction={setNewPassword}
            value={newPassword}
            passwordWarning={newPasswordWarning}
            takeFullWidth={true}
          />
        }
        rightChild={
          <PasswordField
            passwordLabel="New Password Confirmation"
            placeholderText="(required when updating password)"
            setFunction={setNewPasswordConfirm}
            value={newPasswordConfirm}
            passwordWarning={newPasswordConfirmWarning}
            takeFullWidth={true}
          />
        }
      />

      {/* Full name */}
      <RegularField
        fieldLabel="Full Name"
        placeholderText={"e.g., John Smith (optional)"}
        fieldType="text"
        setFunction={setFullName}
        value={fullName}
        fieldWarning={false}
        takeFullWidth={true}
      />

      {/* First name and last name */}
      <DualRowItem
        leftChild={
          <RegularField
            fieldLabel="First Name"
            placeholderText={"e.g., John (required)"}
            fieldType="text"
            setFunction={setFirstName}
            value={firstName}
            fieldWarning={firstNameWarning}
            takeFullWidth={true}
          />
        }
        rightChild={
          <RegularField
            fieldLabel="Last Name"
            placeholderText={"e.g., Smith (required)"}
            fieldType="text"
            setFunction={setLastName}
            value={lastName}
            fieldWarning={lastNameWarning}
            takeFullWidth={true}
          />
        }
      />

      {/* Position title and Physician Work ID */}
      <DualRowItem
        leftChild={
          <RegularField
            fieldLabel="Position Title"
            placeholderText={"e.g., Dermatologist (optional)"}
            fieldType="text"
            setFunction={setPositionTitle}
            value={positionTitle}
            fieldWarning={false}
            takeFullWidth={true}
            fieldDisabled={disableFields}
          />
        }
        rightChild={
          <RegularField
            fieldLabel="Physician Work ID"
            placeholderText={"e.g., 1000000 (optional)"}
            fieldType="text"
            setFunction={setPhysicianWorkId}
            value={physicianWorkId}
            fieldWarning={false}
            takeFullWidth={true}
            fieldDisabled={disableFields}
          />
        }
      />

      {/* Contact number one and contact number two */}
      <DualRowItem
        leftChild={
          <RegularField
            fieldLabel="Primary Telephone"
            placeholderText={"e.g., 1+ 123-456-7890 (optional)"}
            fieldType="tel"
            setFunction={setPhoneOne}
            value={phoneOne}
            fieldWarning={false}
            takeFullWidth={true}
            fieldDisabled={disableFields}
          />
        }
        rightChild={
          <RegularField
            fieldLabel="Secondary Telephone"
            placeholderText={"e.g., 1+ 123-456-7890 (optional)"}
            fieldType="tel"
            setFunction={setPhoneTwo}
            value={phoneTwo}
            fieldWarning={false}
            takeFullWidth={true}
            fieldDisabled={disableFields}
          />
        }
      />

      {/* Address */}
      <RegularField
        fieldLabel="Address"
        placeholderText={"e.g., Some Street 123 (optional)"}
        fieldType="text"
        setFunction={setAddress}
        value={address}
        fieldWarning={false}
        takeFullWidth={true}
        fieldDisabled={disableFields}
      />

      {/* Birth date and sex */}
      <DualRowItem
        leftChild={
          <RegularField
            fieldLabel="Birth Date"
            placeholderText={"Birth Date (optional)"}
            fieldType="date"
            setFunction={setBirthDate}
            value={birthDate}
            fieldWarning={false}
            takeFullWidth={true}
            fieldDisabled={disableFields}
          />
        }
        rightChild={
          <SelectField
            fieldLabel="Sex"
            options={["Male", "Female", "Other"]}
            placeholderText="Select Sex (optional)"
            setFunction={setSex}
            value={sex}
            fieldWarning={false}
            takeFullWidth={true}
            fieldDisabled={disableFields}
          />
        }
      />

      <div className="py-3" />

      <div className="flex flex-row items-center justify-end w-full Xbg-red-500">
        <TextActionButton
          prompt={"Update Profile"}
          actionFunction={handleSubmit}
        />
      </div>
    </div>
  );
};

export default ProfileScreen;
